import React, { useMemo, useState } from "react";
import {
  Button,
  Flex,
  FormControl,
  Text,
  useColorModeValue,
  useToast,
  Box,
  Collapse,
  Progress,
  HStack,
  Icon,
} from "@chakra-ui/react";
import { CheckCircleIcon, WarningIcon } from "@chakra-ui/icons";
import Card from "components/card/Card";
import InputField from "components/fields/InputField";

// Firebase
import { auth } from "../../../../../../firebase";
import {
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";

/* ======= POLÍTICA ======= */
const POLICY = {
  min: 8,
  max: 50,
  requireUpper: true,
  requireLower: true,
  requireNumber: true,
  requireSpecial: true,
};

function checkPolicy(pwd) {
  const upper = /[A-Z]/.test(pwd);
  const lower = /[a-z]/.test(pwd);
  const number = /[0-9]/.test(pwd);
  const special = /[^A-Za-z0-9]/.test(pwd);
  const lenOk = typeof pwd === "string" && pwd.length >= POLICY.min && pwd.length <= POLICY.max;

  // score simples só pra barra de força
  let score = 0;
  if (lenOk) score += 1;
  if (upper) score += 1;
  if (lower) score += 1;
  if (number) score += 1;
  if (special) score += 1;

  return {
    upper, lower, number, special, lenOk,
    score, // 0..5
    allOk:
      (!POLICY.requireUpper || upper) &&
      (!POLICY.requireLower || lower) &&
      (!POLICY.requireNumber || number) &&
      (!POLICY.requireSpecial || special) &&
      lenOk,
  };
}

export default function Password(props) {
  const { ...rest } = props;
  const textColorPrimary = useColorModeValue("secondaryGray.900", "white");
  const textColorSecondary = "secondaryGray.600";
  const panelBg = useColorModeValue("blackAlpha.50", "whiteAlpha.100");
  const toast = useToast();

  const [oldPwd, setOldPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [showPanel, setShowPanel] = useState(false);
  const [loading, setLoading] = useState(false);

  const result = useMemo(() => checkPolicy(newPwd), [newPwd]);

  const strength = useMemo(() => {
    // 0..100
    return Math.min(100, Math.round((result.score / 5) * 100));
  }, [result]);

  const strengthLabel =
    strength < 40 ? "Weak" : strength < 70 ? "Medium" : "Strong";

  const validateBeforeSubmit = () => {
    if (!oldPwd || !newPwd || !confirmPwd) {
      throw new Error("Preencha todos os campos.");
    }
    if (newPwd !== confirmPwd) {
      throw new Error("As senhas não conferem.");
    }
    if (!result.allOk) {
      throw new Error("A nova senha não atende à política de senha.");
    }
    if (newPwd === oldPwd) {
      throw new Error("A nova senha deve ser diferente da antiga.");
    }
  };

  const handleChangePassword = async () => {
    try {
      setLoading(true);
      validateBeforeSubmit();

      const user = auth.currentUser;
      if (!user || !user.email) {
        throw new Error("Usuário não autenticado.");
      }

      // reautentica com a senha antiga
      const cred = EmailAuthProvider.credential(user.email, oldPwd);
      await reauthenticateWithCredential(user, cred);

      // troca de senha
      await updatePassword(user, newPwd);

      setOldPwd("");
      setNewPwd("");
      setConfirmPwd("");
      setShowPanel(false);

      toast({ title: "Senha alterada com sucesso!", status: "success" });
    } catch (e) {
      const msg =
        e?.code === "auth/wrong-password"
          ? "Senha antiga incorreta."
          : e?.code === "auth/weak-password"
          ? "A nova senha é muito fraca."
          : e?.code === "auth/requires-recent-login"
          ? "Faça login novamente para alterar a senha."
          : e?.message || "Não foi possível alterar a senha.";
      toast({ title: "Erro ao alterar senha", description: msg, status: "error" });
    } finally {
      setLoading(false);
    }
  };

  const Item = ({ ok, children }) => (
    <HStack spacing={2} align="center">
      <Icon as={ok ? CheckCircleIcon : WarningIcon} color={ok ? "green.400" : "gray.400"} boxSize={4} />
      <Text fontSize="sm">{children}</Text>
    </HStack>
  );

  return (
    <Card {...rest}>
      <Flex direction="column" mb="30px" ms="10px">
        <Text fontSize="xl" color={textColorPrimary} fontWeight="bold">
          Alterar Senha
        </Text>
      </Flex>

      <FormControl>
        <Flex flexDirection="column">
          <InputField
            mb="25px"
            id="old"
            label="Senha Atual"
            placeholder="Sua senha atual"
            type="password"
            value={oldPwd}
            onChange={(e) => setOldPwd(e.target.value)}
          />

          <InputField
            mb="8px"
            id="new"
            label="Nova Senha"
            placeholder="Sua nova senha"
            type="password"
            value={newPwd}
            onChange={(e) => setNewPwd(e.target.value)}
            onFocus={() => setShowPanel(true)}
          />

          {/* Painel de política abaixo do campo */}
          <Collapse in={showPanel} animateOpacity>
            <Box bg={panelBg} border="1px solid" borderColor="whiteAlpha.200" rounded="md" p={4} mb="20px">
              <Text fontSize="sm" fontWeight="600" mb={2}>
                Política de senha
              </Text>

              <Progress value={strength} size="sm" mb={2} />
              <Text fontSize="xs" color="gray.500" mb={3}>
                Força: {strengthLabel}
              </Text>

              <Flex gap={4} direction={{ base: "column", md: "row" }}>
                <Flex direction="column" gap={2}>
                  <Item ok={result.lenOk}>
                    {`Entre ${POLICY.min} e ${POLICY.max} caracteres`}
                  </Item>
                  {POLICY.requireUpper && <Item ok={result.upper}>Pelo menos 1 letra maiúscula (A-Z)</Item>}
                  {POLICY.requireLower && <Item ok={result.lower}>Pelo menos 1 letra minúscula (a-z)</Item>}
                </Flex>
                <Flex direction="column" gap={2}>
                  {POLICY.requireNumber && <Item ok={result.number}>Pelo menos 1 número (0-9)</Item>}
                  {POLICY.requireSpecial && <Item ok={result.special}>Pelo menos 1 caractere especial (!@#$…)</Item>}
                </Flex>
              </Flex>
            </Box>
          </Collapse>

          <InputField
            mb="25px"
            id="confirm"
            label="Confirmar Nova Senha"
            placeholder="Confirme a nova senha"
            type="password"
            value={confirmPwd}
            onChange={(e) => setConfirmPwd(e.target.value)}
          />
        </Flex>
      </FormControl>

      <Button
        borderRadius="16px"
        minW="183px"
        h="44px"
        ms="auto"
        mt="33px"
        variant="brand"
        color="white"
        fontSize="sm"
        fontWeight="500"
        _hover={{ bg: "brand.600" }}
        _active={{ bg: "brand.500" }}
        _focus={{ bg: "brand.500" }}
        onClick={handleChangePassword}
        isLoading={loading}
        isDisabled={loading}
      >
        Alterar Senha
      </Button>
    </Card>
  );
}
